/**
 * User preferences.
 *
 * Kept in localStorage on this device only: synchronous, available at boot,
 * best-effort when storage is unavailable (private mode).
 */

const LOCAL_PREFIX = 'pm.';

// 'background' stores the chosen effect ({type, blur|id}); the custom
// image itself ('bgImage') is kept apart — data URLs are large.
export const PREF_KEYS = Object.freeze(['name', 'theme', 'micId', 'camId', 'speakerId', 'joinMuted', 'joinCamOff', 'background', 'noiseSuppression']);

export function getLocalPref(key) {
  try {
    const raw = localStorage.getItem(LOCAL_PREFIX + key);
    return raw === null ? undefined : JSON.parse(raw);
  } catch {
    return undefined;
  }
}

/**
 * Meeting-start choices that must never outlive the meeting they were made
 * for: the room type (Closed/Open), lock and passcode. Nothing writes them
 * now; an earlier build kept the room type in `pm.waitingRoom`, which is
 * what this wipes — at boot and on sign-out.
 */
export const MEETING_STATE_KEYS = Object.freeze(['waitingRoom', 'roomType', 'locked', 'passcode']);

export function clearMeetingState() {
  for (const store of ['localStorage', 'sessionStorage']) {
    try {
      for (const key of MEETING_STATE_KEYS) globalThis[store]?.removeItem(LOCAL_PREFIX + key);
    } catch {
      /* storage unavailable (private mode) */
    }
  }
}

export function setLocalPref(key, value) {
  try {
    if (value === undefined || value === null) localStorage.removeItem(LOCAL_PREFIX + key);
    else localStorage.setItem(LOCAL_PREFIX + key, JSON.stringify(value));
  } catch {
    /* storage unavailable (private mode) */
  }
}

/** Persist a preference on this device. */
export function setPref(key, value) {
  setLocalPref(key, value);
}

export function getPref(key, fallback = undefined) {
  const v = getLocalPref(key);
  return v === undefined ? fallback : v;
}

/**
 * Special-case theme: it is read before the app boots, by the boot
 * script (public/theme-boot.js). That script strips quotes before comparing, so the
 * value is stored in the same JSON form as every other preference — one
 * key, one format. Writing a second, bare copy only ever clobbered the
 * first, leaving behind a value getLocalPref() could not read.
 */
export function setThemePref(theme) {
  setPref('theme', theme);
}

/** Accepts the bare string older builds stored, as well as the JSON form. */
export function getThemePref() {
  try {
    const raw = localStorage.getItem('pm.theme');
    if (raw === 'light' || raw === 'dark') return raw;
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return undefined;
}
